import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, MapPin } from 'lucide-react';
import { mockData } from '../mock';
import { useT } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';
import { EditButton } from './admin/EditorShell';
import { SchemaEditor } from './admin/SchemaEditor';

const CD_SCHEMA = [
  { key: 'eventStart', label: 'Dato og klokkeslett nedtellinga går mot (ÅÅÅÅ-MM-DDTHH:MM, norsk tid)' },
  { key: 'dateLabel', label: 'Datotekst under nedtellinga' },
  { key: 'placeLabel', label: 'Stadtekst under nedtellinga' },
  { key: 'doneText', label: 'Tekst som blir vist når nedtellinga er ferdig' },
];
const DEFAULT_CD = { eventStart: mockData.eventStart, dateLabel: '2.–4. april 2027', placeLabel: 'Hjeltefjorden Arena, Øygarden', doneText: 'Refuel Weekend er i gang!' };
const parseStart = (v) => { const d = new Date(v); return Number.isNaN(d.getTime()) ? new Date(mockData.eventStart) : d; };

const getParts = (target) => {
  const diff = Math.max(0, target - Date.now());
  return {
    dagar: Math.floor(diff / 86400000),
    timar: Math.floor((diff / 3600000) % 24),
    minutt: Math.floor((diff / 60000) % 60),
    sekund: Math.floor((diff / 1000) % 60),
    done: diff === 0,
  };
};

const FlipDigit = ({ value }) => {
  const [prev, setPrev] = useState(value);
  const [flipping, setFlipping] = useState(false);
  useEffect(() => {
    if (value === prev) return;
    setFlipping(true);
    const id = setTimeout(() => { setPrev(value); setFlipping(false); }, 420);
    return () => clearTimeout(id);
  }, [value, prev]);
  return (
    <span className="relative inline-block [perspective:600px]">
      <span className={`inline-block tabular-nums ${flipping ? 'animate-[flipOut_0.42s_ease-in_forwards]' : ''}`}>{prev}</span>
      {flipping && <span className="absolute inset-0 inline-block tabular-nums animate-[flipIn_0.42s_ease-out_forwards]">{value}</span>}
    </span>
  );
};

const Cell = ({ label, value, hot }) => {
  const { t } = useT();
  const text = String(value).padStart(2, '0');
  return (
    <div data-testid={`countdown-${label}`} className="relative group">
      <div className={`absolute -inset-px rounded-2xl bg-gradient-to-b from-[#ff2e2e] via-[#ff2e2e]/20 to-transparent ${hot ? 'opacity-100 animate-pulse' : 'opacity-70'}`} />
      <div className="relative min-w-[66px] md:min-w-[124px] rounded-2xl bg-[#0b0611]/95 backdrop-blur-md px-3 py-3.5 md:py-5 text-center overflow-hidden">
        <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,46,46,0.35),transparent_60%)]" />
        <div className={`relative ${text.length > 2 ? "text-3xl" : "text-4xl"} md:text-6xl font-bold text-white leading-none whitespace-nowrap drop-shadow-[0_0_22px_rgba(255,46,46,0.85)]`} style={{ fontFamily: "'Oktah Round', sans-serif" }}>
          <FlipDigit value={text[0]} /><FlipDigit value={text[1]} />{text.length > 2 && <FlipDigit value={text.slice(2)} />}
        </div>
        <div className="relative text-[#ff2e2e] text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold mt-2">{t(label)}</div>
      </div>
    </div>
  );
};

const Sep = () => (
  <div className="flex flex-col justify-center gap-2 md:gap-3 pb-6 md:pb-7">
    <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#ff2e2e] animate-pulse shadow-[0_0_10px_#ff2e2e]" />
    <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#ff2e2e] animate-pulse [animation-delay:500ms] shadow-[0_0_10px_#ff2e2e]" />
  </div>
);

const Countdown = () => {
  const { t } = useT();
  const { content } = useContent();
  const cd = { ...DEFAULT_CD, ...(content.countdown || {}) };
  const target = parseStart(cd.eventStart).getTime();
  const [p, setP] = useState(() => getParts(target));
  const [editing, setEditing] = useState(false);
  const fired = useRef(false);
  useEffect(() => { setP(getParts(target)); fired.current = false; }, [target]);
  useEffect(() => {
    const id = setInterval(() => setP(getParts(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  useEffect(() => {
    if (!p.done || fired.current) return;
    fired.current = true;
    import('canvas-confetti').then(({ default: confetti }) => {
      const end = Date.now() + 4000;
      const frame = () => {
        confetti({ particleCount: 6, angle: 60, spread: 60, origin: { x: 0 }, colors: ['#ff2e2e', '#ffffff', '#ffb400'] });
        confetti({ particleCount: 6, angle: 120, spread: 60, origin: { x: 1 }, colors: ['#ff2e2e', '#ffffff', '#ffb400'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    });
  }, [p.done]);

  if (p.done) {
    return (
      <div data-testid="countdown-live" className="text-center">
        <p className="text-[#ff2e2e] font-bold text-3xl md:text-5xl uppercase tracking-widest drop-shadow-[0_0_30px_rgba(255,46,46,0.8)] animate-pulse" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(cd.doneText)}</p>
        <div className="mt-4"><EditButton onClick={() => setEditing(true)} label="Rediger nedtelling" testId="edit-countdown-button" /></div>
        {editing && <SchemaEditor contentKey="countdown" title="Rediger nedtellinga" single initial={cd} schema={CD_SCHEMA} onClose={() => setEditing(false)} />}
      </div>
    );
  }

  const hot = p.dagar === 0;
  return (
    <div data-testid="countdown" className="relative flex flex-col items-center gap-4">
      <div className={`absolute -inset-6 md:-inset-10 rounded-[3rem] bg-[#ff2e2e]/15 blur-3xl pointer-events-none ${hot ? 'animate-pulse' : ''}`} />
      <div className="relative flex items-stretch gap-1.5 md:gap-3 px-2">
        <Cell label="dagar" value={p.dagar} hot={hot} />
        <Sep />
        <Cell label="timar" value={p.timar} hot={hot} />
        <Sep />
        <Cell label="minutt" value={p.minutt} hot={hot} />
        <Sep />
        <Cell label="sekund" value={p.sekund} hot={hot} />
      </div>
      <div className="relative flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-white/70 text-xs md:text-sm font-semibold">
        <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} className="text-[#ff2e2e]" /> {cd.dateLabel}</span>
        <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#ff2e2e]" /> {cd.placeLabel}</span>
      </div>
      <EditButton onClick={() => setEditing(true)} label="Rediger nedtelling" testId="edit-countdown-button" />
      {editing && <SchemaEditor contentKey="countdown" title="Rediger nedtellinga" single initial={cd} schema={CD_SCHEMA} onClose={() => setEditing(false)} />}
    </div>
  );
};

export default Countdown;
